<template>
  <ValidationObserver
    ref="dialogObserver"
    slim="slim"
  >
    <AppDialog
      class="remove-multiple-schedules-dialog"
      title="Delete shifts"
      size="smedium"
      position="centered"
      show="show"
      append-to-body="append-to-body"
      @close="closeDialog"
    >
      <form
        id="attendance-schedule-form"
        autocomplete="off"
        @submit.prevent="onFormSubmit"
      >
        <div id="form-container">
          <div class="row">
            <div
              class="col-sm-12 mb-15"
              :class="{ 'has-error': fieldError('user_ids') }"
            >
              <AttendanceSchedulesMemberSelect
                :form="form"
                @input="onMembersUpdate"
              />
              <span
                v-show="fieldError('user_ids')"
                class="help-block"
              >
                {{ fieldError('user_ids') }}
              </span>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <AppSwitch
                v-model="form.deleteAll"
                class="cursor-pointer"
                label="Delete all future and past shifts"
                @change="onDeleteAllChange"
              />
            </div>
          </div>
          <div
            v-if="form.deleteAll"
            class="shift-warning"
            :class="{ orange: hasNoShifts }"
          >
            <div class="hi hi-warning hi-12"></div>
            <div>{{ deleteAllWarning }}</div>
          </div>
          <div
            v-else
            class="row from-to"
          >
            <div class="col-sm-6">
              <div class="row">
                <div class="col-sm-12">
                  <label
                    class="control-label"
                    required
                  >
                    From
                  </label>
                </div>
                <div class="col-sm-12 form-inline">
                  <div class="row">
                    <div
                      class="col-sm-12 attendance-schedule-datepicker"
                      :class="{ 'has-error': fieldError('start_date') }"
                    >
                      <AppDatePicker
                        v-model="form.startDate"
                        :min-date="minDate"
                        :start-week-on="startWeekOn"
                        hide-today="hide-today"
                        disable-future="disable-future"
                        :show-quick-pick="false"
                        extra-picker-classes="attendance-schedule-form"
                        type="daily"
                        @input="onStartDateChange"
                      ></AppDatePicker>
                      <span
                        v-show="fieldError('start_date')"
                        class="help-block"
                      >
                        {{ fieldError('start_date') }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="row">
                <div class="col-sm-12">
                  <label
                    class="control-label"
                    required
                  >
                    To
                  </label>
                </div>
                <div class="col-sm-12 form-inline">
                  <div class="row">
                    <div
                      class="col-sm-12 attendance-schedule-datepicker"
                      :class="{ 'has-error': fieldError('stop_date') }"
                    >
                      <AppDatePicker
                        v-model="form.stopDate"
                        :min-date="minDate"
                        :start-week-on="startWeekOn"
                        hide-today="hide-today"
                        disable-future="disable-future"
                        :show-quick-pick="false"
                        extra-picker-classes="attendance-schedule-form"
                        type="daily"
                        @input="onStopDateChange"
                      ></AppDatePicker>
                      <span
                        v-show="fieldError('stop_date')"
                        class="help-block"
                      >
                        {{ fieldError('stop_date') }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-sm-12">
              <AppSwitch
                v-model="form.deleteFutureRecurringShifts"
                class="cursor-pointer"
                label="Delete future recurring shifts"
                @change="onDeleteFutureRecurringShiftsChange"
              />
            </div>
            <div class="col-sm-12">
              <div
                v-if="anyoneSelected()"
                class="shift-warning orange"
              >
                <div class="hi hi-warning hi-12"></div>
                <div>{{ shiftDeletionWarning }}</div>
              </div>
            </div>
            <div class="col-sm-12">
              <div
                v-if="showRecurringDeletionWarning"
                id="recurring-shift-deletion-warning"
                class="shift-warning"
              >
                <div class="hi hi-warning hi-12"></div>
                <div>This will delete all future instances of {{ scheduleCountText }}</div>
              </div>
            </div>
          </div>
        </div>
      </form>
      <template #footer>
        <AppButton @click="closeDialog"> Cancel </AppButton>
        <AppButton
          type="danger"
          :disabled="disabledDeleteButton"
          @click="onFormSubmit"
        >
          Delete shifts
        </AppButton>
      </template>
    </AppDialog>
  </ValidationObserver>
</template>

<script>
import { mapInertiaProps } from '~/utils/inertia';
import { applyTemplate } from '~/utils/url_template';
import AttendanceSchedulesMemberSelect from '../AttendanceSchedulesMemberSelect.vue';
import { AttendanceSchedulesResource } from '~/resources';
import { ATTENDANCE_SCHEDULES_URL } from '~/app_constants';
import { pluralize } from '~/utils/string_utils';

export default {
  name: 'RemoveMultipleAttendanceSchedulesDialog',
  components: {
    AttendanceSchedulesMemberSelect,
  },
  props: {
    filteredUsers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: this.$inertia.form({
        userId: [],
        excludedUserIds: null,
        selectAllUsers: false,
        deleteAll: false,
        startDate: moment(),
        stopDate: moment(),
        deleteFutureRecurringShifts: false,
        errors: this.errors,
      }),
      shiftDeletionWarning: null,
      showRecurringDeletionWarning: false,
      disabledDeleteButton: false,
      scheduleCount: null,
      scheduleCountText: '',
      shiftCount: 0,
      dateRange: `on ${moment().format('MMM DD, YYYY')}`,
    };
  },
  computed: {
    ...mapInertiaProps({
      organization: 'organization',
      minDate: 'min_date',
      startWeekOn: 'start_week_on',
      errors: 'errors',
      users: 'calendar_resources.users',
      activeView: 'active_view',
      userIds: 'filtered_user_ids',
    }),
    deleteAllWarning() {
      return this.hasNoShifts
        ? 'There are no shifts to delete'
        : 'This will delete all future and past shifts for selected members';
    },
    hasNoShifts() {
      return this.shiftCount === 0;
    },
  },
  created() {
    this.updateDeleteButtonState();
  },
  methods: {
    countShifts() {
      const payload = {
        user_ids: this.form.userId.join('-'),
        excluded_user_ids: this.form.excludedUserIds,
        select_all_users: this.form.selectAllUsers,
        organization_id: this.organization.id,
        start_date: this.form.startDate.format('YYYY-MM-DD'),
        stop_date: this.deleteAll ? null : this.form.stopDate.format('YYYY-MM-DD'),
      };

      AttendanceSchedulesResource.countShifts({ organizationId: this.organization.id }, payload)
        .then((response) => {
          if (response.shift_count === 0) {
            this.shiftDeletionWarning = 'There are no shifts to delete';
            this.showRecurringDeletionWarning = false;
          } else {
            this.shiftDeletionWarning = `This will delete ${response.shift_count} ${pluralize(
              response.shift_count,
              'shift',
            )} ${this.dateRange}`;
            if (this.form.deleteFutureRecurringShifts) {
              this.showRecurringDeletionWarning = true;
            }
          }

          this.scheduleCount = response.schedule_count;
          this.scheduleCountText =
            response.schedule_count === 1
              ? `${response.schedule_count} recurring shift`
              : `${response.schedule_count} recurring shifts`;
          this.form.errors = typeof response.errors === 'undefined' ? {} : response.errors;
          this.shiftCount = response.shift_count;
          this.updateDeleteButtonState(response.shift_count);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    fieldError(field) {
      if (this.form.errors && this.form.errors[field]) {
        return this.form.errors[field][0];
      }
    },
    closeDialog(id = null) {
      this.$emit('close', id);
    },
    anyoneSelected() {
      return this.form.userId?.length > 0 || this.form.selectAllUsers;
    },
    onMembersUpdate(value) {
      this.form.userId = value;
      this.form.selectAllUsers = this.value?.length === this.userIds?.length;

      this.countShifts();
    },
    onDeleteAllChange(value) {
      this.deleteAll = value;

      this.countShifts();
    },
    onStartDateChange(start) {
      this.form.startDate = start;
      if (start > this.form.stopDate) {
        this.form.stopDate = start;
      }
      this.checkDateRange();
      this.countShifts();
    },
    onStopDateChange(stop) {
      this.form.stopDate = stop;
      if (this.form.startDate > stop) {
        this.form.startDate = stop;
      }
      this.checkDateRange();
      this.countShifts();
    },
    onDeleteFutureRecurringShiftsChange() {
      if (this.form.deleteFutureRecurringShifts === true && this.scheduleCount > 0) {
        this.showRecurringDeletionWarning = true;
        this.disabledDeleteButton = false;
      } else {
        this.showRecurringDeletionWarning = false;
        this.countShifts();
      }
    },
    updateDeleteButtonState(shiftCount = null) {
      if (!this.anyoneSelected()) {
        return (this.disabledDeleteButton = true);
      }

      this.disabledDeleteButton = shiftCount === 0;
    },
    checkDateRange() {
      const start = this.form.startDate.format('MMM DD, YYYY');
      const stop = this.form.stopDate.format('MMM DD, YYYY');

      if (this.form.stopDate > this.form.startDate) {
        this.dateRange = `between ${start} and ${stop}`;
      } else {
        this.dateRange = `on ${start}`;
      }
    },
    onFormSubmit() {
      const { origin } = window.location;
      const url = new URL(
        applyTemplate(`${ATTENDANCE_SCHEDULES_URL}/bulk_destroy`, {
          organizationId: this.organization.id,
        }),
        origin,
      );

      url.searchParams.set('active_view', this.activeView);
      const attendanceSchedulesUrlString = url.toString();

      this.form.transform((data) => {
        const payload = {};

        payload.delete_all = data.deleteAll;
        payload.start_date = data.startDate.format('YYYY-MM-DD');
        payload.stop_date = data.stopDate.format('YYYY-MM-DD');
        payload.delete_future_recurring_shifts = data.deleteFutureRecurringShifts;
        payload.select_all_users = data.selectAllUsers;
        payload.excluded_user_ids = data.excludedUserIds;
        payload.filtered_user_ids = (this.users || []).map((user) => user.id);

        if (data.userId === null) {
          if (this.members.length > 0) {
            payload.user_ids = [this.members[0].id];
          } else {
            payload.user_ids = [];
          }
        } else if (data.userId.length > 1) {
          payload.user_ids = data.userId;
        } else {
          if (Array.isArray(data.userId)) {
            payload.user_ids = data.userId;
          } else {
            payload.user_ids = [data.userId];
          }
        }

        if (payload.user_ids) {
          payload.user_ids = payload.user_ids.join('-');
        }

        return payload;
      });

      return this.form.delete(attendanceSchedulesUrlString, { onSuccess: () => this.closeDialog() });
    },
  },
};
</script>
